import { Dialog, DialogContent } from '@material-ui/core';
import classnames from 'classnames';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { BilingualVideoValue, VideoLanguage } from './bilingual';
import { VideoImportProps, VideoImportValue, VideoModalStep } from './types';
import { deleteVideoByUrl, uploadVideo } from './uploadVideo';
import {
  DEFAULT_MAX_UPLOAD_BYTES,
  isYoutubeUrl as defaultIsYoutubeUrl,
  toBinaryFlag,
} from './utils';
import { useVideoImportStyles } from './VideoImport.styles';
import VideoImportChoiceModal from './VideoImportChoiceModal';
import VideoImportUploadModal from './VideoImportUploadModal';
import VideoImportYoutubeModal from './VideoImportYoutubeModal';
import { VideoLanguageTabs } from './VideoModalShared';

type Draft = {
  url: string;
  sound: boolean;
  auto: boolean;
  file: File | null;
  preview: string;
};

const blankDraft = (): Draft => ({ url: '', sound: false, auto: false, file: null, preview: '' });

const draftFromValue = (
  video?: string | null,
  sound?: number | null,
  auto?: number | null
): Draft => {
  const existing = video || '';
  const youtube = !!existing && defaultIsYoutubeUrl(existing);
  return {
    url: youtube ? existing : '',
    sound: sound === 1,
    auto: auto === 1,
    file: null,
    preview: existing && !youtube ? existing : '',
  };
};

const BilingualVideoImport: FC<VideoImportProps> = ({
  open,
  onClose,
  onSubmit,
  initialValue,
  enableYoutube = true,
  enableUpload = true,
  onUploadFile = uploadVideo,
  maxUploadBytes = DEFAULT_MAX_UPLOAD_BYTES,
  validateYoutubeUrl = defaultIsYoutubeUrl,
  youtubeInputId = 'video-import-youtube-url',
  showYoutubePreview = true,
  languages = ['fr', 'en'],
  initialLanguage = 'fr',
  lockedSource,
  submitLabel = 'Valider',
  inline = false,
}) => {
  const classes = useVideoImportStyles();
  const [step, setStep] = useState<VideoModalStep>('closed');
  const [language, setLanguage] = useState<VideoLanguage>(initialLanguage);
  const [drafts, setDrafts] = useState<Record<VideoLanguage, Draft>>({
    fr: blankDraft(),
    en: blankDraft(),
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const blobsRef = useRef<string[]>([]);

  const revokeBlobs = () => {
    blobsRef.current.forEach(url => URL.revokeObjectURL(url));
    blobsRef.current = [];
  };

  useEffect(() => {
    if (!open) {
      revokeBlobs();
      setStep('closed');
      setUploadError('');
      setSubmitError('');
      setIsUploading(false);
      return;
    }
    const value = initialValue as BilingualVideoValue | undefined;
    setDrafts({
      fr: draftFromValue(value?.video, value?.muteVideo, value?.autoPlayVideo),
      en: draftFromValue(value?.videoEn, value?.muteVideoEn, value?.autoPlayVideoEn),
    });
    setLanguage(languages.includes(initialLanguage) ? initialLanguage : languages[0]);
    setUploadError('');
    setSubmitError('');
    setIsUploading(false);
    setUploadProgress(0);
    if (lockedSource) setStep(lockedSource);
    else if (enableYoutube && !enableUpload) setStep('youtube');
    else if (!enableYoutube && enableUpload) setStep('upload');
    else setStep('choice');
    // Reset only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const draft = drafts[language];
  const update = (patch: Partial<Draft>) =>
    setDrafts(previous => ({ ...previous, [language]: { ...previous[language], ...patch } }));

  const rememberBlob = (url: string) => {
    blobsRef.current.push(url);
  };

  const close = () => {
    revokeBlobs();
    setStep('closed');
    onClose();
  };

  const populated = (lang: VideoLanguage) =>
    step === 'upload' || lockedSource === 'upload'
      ? !!(drafts[lang].file || drafts[lang].preview)
      : !!drafts[lang].url;

  const youtubeReady =
    languages.some(lang => validateYoutubeUrl(drafts[lang].url)) &&
    languages.every(lang => !drafts[lang].url || validateYoutubeUrl(drafts[lang].url));
  const uploadReady = languages.some(populated);

  const resolveUrl = async (lang: VideoLanguage) => {
    const current = drafts[lang];
    if (current.file) return onUploadFile(current.file, { onProgress: setUploadProgress });
    if (step === 'upload' || lockedSource === 'upload') return current.preview;
    return current.url;
  };

  const submit = async () => {
    setSubmitError('');
    setUploadError('');
    setIsUploading(true);
    try {
      const [video, videoEn] = await Promise.all([resolveUrl('fr'), resolveUrl('en')]);
      const value: VideoImportValue = {
        video,
        videoEn,
        autoPlayVideo: toBinaryFlag(drafts.fr.auto),
        muteVideo: toBinaryFlag(drafts.fr.sound),
        autoPlayVideoEn: toBinaryFlag(drafts.en.auto),
        muteVideoEn: toBinaryFlag(drafts.en.sound),
      };
      await onSubmit(value);
      blobsRef.current = [];
      onClose();
    } catch {
      const message = "Échec de l'enregistrement. Veuillez réessayer.";
      if (step === 'upload' || lockedSource === 'upload') setUploadError(message);
      else setSubmitError(message);
      setIsUploading(false);
    }
  };

  const savedVideo = {
    fr: !!initialValue?.video,
    en: !!initialValue?.videoEn,
  };
  const tabs = (
    <VideoLanguageTabs
      languages={languages}
      language={language}
      onChange={setLanguage}
      missing={{
        fr: !savedVideo.fr && savedVideo.en,
        en: !savedVideo.en && savedVideo.fr,
      }}
    />
  );
  const back = () => {
    if (!lockedSource && enableYoutube && enableUpload && !inline) {
      setStep('choice');
      return;
    }
    close();
  };

  const form = (
    <>
      {step === 'choice' && (
        <VideoImportChoiceModal
          onClose={close}
          enableYoutube={enableYoutube}
          enableUpload={enableUpload}
          onSelectYoutube={() => setStep('youtube')}
          onSelectUpload={() => setStep('upload')}
        />
      )}
      {step === 'youtube' && (
        <VideoImportYoutubeModal
          onClose={close}
          onBack={back}
          url={draft.url}
          onUrlChange={url => update({ url })}
          youtubeInputId={`${youtubeInputId}-${language}`}
          showYoutubePreview={showYoutubePreview}
          validateYoutubeUrl={validateYoutubeUrl}
          submitError={submitError}
          onValidate={submit}
          tabs={tabs}
          showHeader={!inline}
          submitLabel={isUploading ? 'Enregistrement…' : submitLabel}
          validateDisabled={!youtubeReady || isUploading}
          mute={draft.sound}
          setMute={sound => update({ sound })}
          checkedAuto={draft.auto}
          setCheckedAuto={auto => update({ auto })}
        />
      )}
      {step === 'upload' && (
        <VideoImportUploadModal
          onClose={close}
          onBack={back}
          videoFile={draft.file}
          uploadedVideoUrl={draft.preview}
          uploadProgress={uploadProgress}
          onVideoFileChange={file => {
            if (!file) {
              update({ file: null, preview: '' });
              return;
            }
            const preview =
              typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '';
            if (preview) rememberBlob(preview);
            update({ file, preview });
          }}
          onDeleteUploadedVideo={() => update({ file: null, preview: '' })}
          uploadError={uploadError}
          onUploadError={setUploadError}
          maxUploadBytes={maxUploadBytes}
          isUploading={isUploading}
          onValidate={submit}
          tabs={tabs}
          showHeader={!inline}
          submitLabel={submitLabel}
          validateDisabled={!uploadReady || isUploading}
          fileInputLabel={`Fichier vidéo — ${language === 'en' ? 'Anglais' : 'Français'}`}
          previewTitle={draft.file?.name || 'Vidéo importée'}
          mute={draft.sound}
          setMute={sound => update({ sound })}
          checkedAuto={draft.auto}
          setCheckedAuto={auto => update({ auto })}
        />
      )}
    </>
  );

  if (inline) return <div className={classes.videoModalStep}>{form}</div>;

  const isSizedStep = step === 'choice' || step === 'youtube' || step === 'upload';
  return (
    <Dialog
      open={open}
      onClose={close}
      scroll="body"
      aria-labelledby="video-import-dialog"
      classes={{
        root: classes.videoModalRoot,
        paper: classnames(classes.videoModalPaper, {
          [classes.videoModalPaperSized]: isSizedStep,
          [classes.videoModalPaperForm]: step === 'youtube' || step === 'upload',
        }),
      }}>
      <DialogContent className={classes.videoModalContent}>{form}</DialogContent>
    </Dialog>
  );
};

const SingleVideoImport: FC<VideoImportProps> = ({
  open,
  onClose,
  onSubmit,
  initialValue,
  enableYoutube = true,
  enableUpload = true,
  deferUpload = false,
  onUploadFile = uploadVideo,
  onDeleteUpload = deleteVideoByUrl,
  maxUploadBytes = DEFAULT_MAX_UPLOAD_BYTES,
  validateYoutubeUrl = defaultIsYoutubeUrl,
  youtubeInputId = 'video-import-youtube-url',
  showYoutubePreview = true,
}) => {
  const classes = useVideoImportStyles();
  const [step, setStep] = useState<VideoModalStep>('closed');
  const [urlTemp, setUrlTemp] = useState('');
  const [mute, setMute] = useState(false);
  const [checkedAuto, setCheckedAuto] = useState(false);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const uploadAbortRef = useRef<AbortController | null>(null);
  const uploadRequestIdRef = useRef(0);
  const blobUrlRef = useRef<string | null>(null);
  // URL already stored on the server that must be removed if the user discards it
  // (immediate-upload mode only; deferred uploads never touch the server here).
  const orphanUrlRef = useRef<string | null>(null);

  const resolveInitialStep = useCallback((): VideoModalStep => {
    if (enableYoutube && !enableUpload) return 'youtube';
    if (!enableYoutube && enableUpload) return 'upload';
    return 'choice';
  }, [enableYoutube, enableUpload]);

  const getYoutubeInitialUrl = useCallback(
    (video?: string | null) => (video && validateYoutubeUrl(video) ? video : ''),
    [validateYoutubeUrl]
  );

  const revokeBlobUrl = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  const resetUploadState = useCallback(() => {
    uploadAbortRef.current?.abort();
    uploadAbortRef.current = null;
    revokeBlobUrl();
    setVideoFile(null);
    setPreviewUrl('');
    setUploadProgress(0);
    setIsUploading(false);
  }, []);

  const discardOrphanUpload = useCallback(async () => {
    uploadAbortRef.current?.abort();
    uploadAbortRef.current = null;
    const url = orphanUrlRef.current;
    orphanUrlRef.current = null;
    if (!url) return;
    try {
      await onDeleteUpload(url);
    } catch {
      setUploadError('Échec de la suppression du fichier téléversé.');
    }
  }, [onDeleteUpload]);

  useEffect(() => {
    if (!open) {
      orphanUrlRef.current = null;
      resetUploadState();
      setStep('closed');
      setUploadError('');
      setSubmitError('');
      return;
    }
    setUrlTemp(getYoutubeInitialUrl(initialValue?.video));
    setMute(initialValue?.muteVideo === 1);
    setCheckedAuto(initialValue?.autoPlayVideo === 1);
    orphanUrlRef.current = null;
    resetUploadState();
    setUploadError('');
    setSubmitError('');
    setStep(resolveInitialStep());
    // Sync only when the dialog opens; avoid resetting while the user edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const closeAfterSubmit = () => {
    resetUploadState();
    setStep('closed');
    setUploadError('');
    setSubmitError('');
    onClose();
  };

  const handleClose = () => {
    discardOrphanUpload().catch(() => undefined);
    closeAfterSubmit();
  };

  const buildValue = (video: string, file?: File | null) => ({
    video,
    autoPlayVideo: toBinaryFlag(checkedAuto),
    muteVideo: toBinaryFlag(mute),
    videoFile: file || undefined,
  });

  const handleYoutubeSubmit = async () => {
    setSubmitError('');
    try {
      await onSubmit(buildValue(urlTemp));
      closeAfterSubmit();
    } catch {
      setSubmitError("Échec de l'enregistrement. Veuillez réessayer.");
    }
  };

  const handleVideoFileChange = async (file: File | null) => {
    uploadRequestIdRef.current += 1;
    const requestId = uploadRequestIdRef.current;

    await discardOrphanUpload();
    resetUploadState();
    setUploadError('');
    if (!file) return;

    if (deferUpload) {
      const blobUrl = URL.createObjectURL(file);
      blobUrlRef.current = blobUrl;
      setVideoFile(file);
      setPreviewUrl(blobUrl);
      return;
    }

    setVideoFile(file);
    setIsUploading(true);
    setUploadProgress(0);
    const abortController = new AbortController();
    uploadAbortRef.current = abortController;

    try {
      const url = await onUploadFile(file, {
        onProgress: setUploadProgress,
        signal: abortController.signal,
      });
      if (requestId !== uploadRequestIdRef.current) return;
      orphanUrlRef.current = url;
      setPreviewUrl(url);
      setUploadProgress(100);
    } catch (error) {
      if (error?.name === 'AbortError' || requestId !== uploadRequestIdRef.current) return;
      setUploadError('Échec du téléversement. Veuillez réessayer.');
      setVideoFile(null);
    } finally {
      if (requestId === uploadRequestIdRef.current) {
        setIsUploading(false);
        uploadAbortRef.current = null;
      }
    }
  };

  const handleUploadDelete = () => {
    discardOrphanUpload().catch(() => undefined);
    resetUploadState();
    setUploadError('');
  };

  const handleUploadSubmit = async () => {
    if (!previewUrl) return;
    setUploadError('');
    try {
      await onSubmit(buildValue(previewUrl, videoFile));
      // The uploaded file is now in use: keep it on the server.
      orphanUrlRef.current = null;
      closeAfterSubmit();
    } catch {
      setUploadError("Échec de l'enregistrement. Veuillez réessayer.");
    }
  };

  const handleBack = () => {
    discardOrphanUpload().catch(() => undefined);
    resetUploadState();
    setSubmitError('');
    if (enableYoutube && enableUpload) {
      setStep('choice');
      return;
    }
    closeAfterSubmit();
  };

  const checkboxState = { mute, setMute, checkedAuto, setCheckedAuto };
  const isSizedStep = step === 'choice' || step === 'youtube' || step === 'upload';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="body"
      aria-labelledby="video-import-dialog"
      classes={{
        root: classes.videoModalRoot,
        paper: classnames(classes.videoModalPaper, {
          [classes.videoModalPaperSized]: isSizedStep,
          [classes.videoModalPaperForm]: step === 'youtube' || step === 'upload',
        }),
      }}>
      <DialogContent className={classes.videoModalContent}>
        {step === 'choice' && (
          <VideoImportChoiceModal
            onClose={handleClose}
            enableYoutube={enableYoutube}
            enableUpload={enableUpload}
            onSelectYoutube={() => {
              discardOrphanUpload().catch(() => undefined);
              resetUploadState();
              setUrlTemp(getYoutubeInitialUrl(initialValue?.video));
              setSubmitError('');
              setStep('youtube');
            }}
            onSelectUpload={() => {
              resetUploadState();
              setUploadError('');
              setStep('upload');
            }}
          />
        )}
        {step === 'youtube' && (
          <VideoImportYoutubeModal
            onClose={handleClose}
            onBack={handleBack}
            url={urlTemp}
            onUrlChange={setUrlTemp}
            youtubeInputId={youtubeInputId}
            showYoutubePreview={showYoutubePreview}
            validateYoutubeUrl={validateYoutubeUrl}
            submitError={submitError}
            onValidate={handleYoutubeSubmit}
            {...checkboxState}
          />
        )}
        {step === 'upload' && (
          <VideoImportUploadModal
            onClose={handleClose}
            onBack={handleBack}
            videoFile={videoFile}
            uploadedVideoUrl={previewUrl}
            uploadProgress={uploadProgress}
            onVideoFileChange={handleVideoFileChange}
            onDeleteUploadedVideo={handleUploadDelete}
            uploadError={uploadError}
            onUploadError={setUploadError}
            maxUploadBytes={maxUploadBytes}
            isUploading={isUploading}
            onValidate={handleUploadSubmit}
            {...checkboxState}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const VideoImport: FC<VideoImportProps> = ({ bilingual, ...rest }) =>
  bilingual ? <BilingualVideoImport {...rest} /> : <SingleVideoImport {...rest} />;

export default VideoImport;
export type { BilingualVideoValue, VideoLanguage } from './bilingual';
export {
  emptyVideo,
  localizedVideo,
  setVideoVersion,
  videoLanguages,
  videoVersion,
} from './bilingual';
export type { VideoImportProps, VideoImportValue } from './types';
export { deleteVideoByUrl, uploadVideo } from './uploadVideo';
export { formatMaxUploadMo, isYoutubeUrl, toBinaryFlag } from './utils';
